<template>
  <main>
    <Trail
      :list="[
        {
          path: '/proyectos/investigacion',
          name: 'Projects',
          tag: 'Proyectos de Investigación'
        },
        {
          name: 'SCOR',
          tag: 'SCOR'
        }
      ]"
    />
    <Container class="container--main">
      <Title tag="h1" align="center">
        SCOR Working Group I55
      </Title>
      <Title tag="h4" theme="secondary" align="center">
        Eastern Boundary Upwelling Systems (EBUS): Diversity, Coupled Dynamics
        and Sensitivity to Climate Change
      </Title>
      <Figure>
        <Image :srcsets="['/images/proyectos/scor/titulo.png']" />
      </Figure>
      <br />
      <br />
      <Row>
        <ColumnUi md="9">
          <Navtab id="preamble">
            <Title tag="h2">
              Preamble
            </Title>
            <Figure>
              <Image
                style="width: 100%; height: auto;"
                :srcsets="['/images/proyectos/scor/corrientes_world_dif.png']"
                title="Cogollo Estero Camarones (SNLMT)"
              />
            </Figure>
            <p>
              Eastern Boundary Upwelling Systems (EBUS) (California, Humboldt,
              Benguela and Canary Current System as major systems and, e.g.,
              Iberian, Leeuwin and West Coast of India as minor ones) are
              characterized by complex dynamical processes spanning a wide range
              of spatio-temporal variability due to the strong coupling between
              the ocean and atmosphere. They are among the most productive
              marine ecosystems in the world, supporting some of the world’s
              major fisheries, yet occupying only 0.1% of the global surface.
              Trade winds flowing equatorward produce coastal upwelling, which
              uplifts cold and nutrient-rich waters to the surface, where
              favorable light conditions sustain high phytoplankton growth.
              Below the surface, intense Oxygen Minimum Zones develop due to
              high organic matter export, in conjunction with a sluggish
              ventilation. When these waters upwell, they release CO2 and N2O,
              potent greenhouse gases, to the atmosphere. Locally, the mesoscale
              low-level atmospheric circulation is affected by air-sea-land
              interactions, which impact the upwelling and productivity, while
              remote forcing can modulate upwelling at timescales from
              intraseasonal to interdecadal and longer. EBUS are natural
              laboratories for studying the breadth of interactive processes
              between land, ocean and atmosphere at the regional scale.
              <strong
                ><a href="https://scor-int.org/group/155/" target="_blank"
                  >This SCOR Working Group 155
                </a></strong
              >‘Eastern Boundary Upwelling Systems (EBUS): Diversity, Coupled
              Dynamics and Sensitivity to Climate Change’ is focused on
              reviewing the existing knowledge on EBUS and formulate a strategic
              recommendation white paper for setting up regional observational
              systems and climate modeling approaches to monitor and understand
              physical and biogeochemical ocean-atmosphere processes. These
              observational systems will be instrumental in improving the
              performance and reliability of climate models in these
              socio-economically relevant regions of the world ocean.
            </p>
          </Navtab>
          <Navtab id="terms-reference">
            <Title tag="h2">
              Terms of Reference
            </Title>
            <p>Based on the following goals:</p>
            <List tag="ol">
              <li>
                To identify key feedbacks mechanisms, establish similarities,
                differences and knowledge gaps across all EBUS,
              </li>
              <li>
                To facilitate the access to all developed scientific information
                across all EBUS,
              </li>
              <li>
                To identify the current limitations of numerical models and
                necessary improvements,
              </li>
            </List>
            <p>the terms of reference (ToRs) are:</p>
            <p>
              <strong>
                ToR#1.
              </strong>
              <strong>Synthesize available information</strong> on important
              EBUS processes, their time and space scales (i.e., diurnal,
              intraseasonal, interannual, decadal, multidecadal) and their
              implications on water column properties, biogeochemical cycles,
              biodiversity/ecosystem structure and functioning, and the
              ecosystem services they provide. Seek to identify key feedback
              mechanisms, establish similarities, differences and knowledge gaps
              across all EBUS. A scientific review paper and a summary for
              policy makers will be the key deliverable.
            </p>
            <p>
              <strong>ToR#2. Develop a Web portal for EBUS</strong> by creating
              a web-based platform to graphically query integrated information
              on published data, model outputs, as well as protocols for
              measuring key properties and indicators in EBUS.
            </p>
            <p>
              <strong>
                ToR#3. Determine the strengths and weaknesses of existing EBUS
                coupled physical-biological models.
              </strong>
              Such an analysis will have mostly a regional focus, but it will
              also attempt to address subregional scales building upon past and
              on-going research programs on upwelling centers (e.g. Bay of Hann
              near Dakar (Senegal), Monterey Bay (USA), Bay of Concepcion
              (Chile)). This TOR will require interaction with the CLIVAR WG.
              The key product will be a publication in a high impact journal.
            </p>
            <p>
              <strong>
                ToR#4. Recommend a framework for regional interdisciplinary
                (physics to biology) EBUS observing and modeling systems.
              </strong>
              This stems from TORs 1-3. The observation system will be designed
              to improve the performance and reliability of forecast models in
              these socio-economically relevant regions of the world ocean. Such
              a recommendation brief will also address needs for fostering
              interactions between the observational and modelling communities
              (e.g. coordinated experiments with common forcing; recommendations
              on resolution of specific processes or a specific scale, etc). The
              recommendations will be included in the summary for policy makers.
            </p>
            <p>
              Based on the terms of reference, deliverables are:
            </p>
            <p>
              <strong>Deliverable #1:</strong> A scientific review paper on key
              feedback mechanisms and knowledge gaps across all EBUS, and which
              presents similarities and differences amongst EBUS. The paper is
              also meant to identifying research lines that can help in bridging
              the gap between regional and local/coastal scales from a modeling
              and observational perspective.
            </p>
            <p>
              <strong>Deliverable #2: </strong>A web-based platform dedicated to
              providing information on meta-data (data and/or associated
              databases with the responsible scientist/people on charge if it is
              available), key published papers from observations and model
              results (including information on reanalyzes used for forcing
              regional models), where graphically the EBUS databases will be
              queried and useful information about measurements and protocols
              <br />
            </p>
            <p>
              <strong>Deliverable #3: </strong>A high impact publication on
              comparing EBUS based on modelling results.
            </p>
            <p>
              <strong>Deliverable #4:</strong> A summary for policy makers,
              where a strategic recommendation on how to more cost effectively
              design and improve regional observational systems and synthesize
              available information are presented.
            </p>
          </Navtab>
          <Navtab id="members">
            <Title tag="h2">
              Members
            </Title>
            <Title tag="h3">Full members</Title>
            <List marked>
              <li>
                Ivonne Montes (IGP Instituto Geofísico del Perú, Perú) co-chair
              </li>
              <li>
                Ruben Escribano (UdeC Universidad de Concepción, Chile) co-chair
              </li>
              <li>
                Francisco Chavez (MBARI Monterey Bay Aquarium Research
                Institute, USA)
              </li>
              <li>
                Salvador Lluch-Cota (CONACYT Centro de Investigaciones
                Biológicas del Noreste, Mexico)
              </li>
              <li>
                Enrique Curchitser (Department of Environmental Sciences School
                of Environmental and Biological Sciences, USA)
              </li>
              <li>
                Baye Cheikh Mbaye (University of Dakar, Senegal)
              </li>
              <li>
                Boris Dewitte (CEAZA Center for Advanced Studies in Arid Zones,
                Chile)
              </li>
              <li>
                Andreas Oschlies (GEOMAR Helmholtz Centre for Ocean Research
                Kiel, Germany)
              </li>
              <li>
                Sarah Fawcett (University of Cape Town, South Africa),
              </li>
              <li>
                Parv Suntharalingam (University of East Anglia, United Kingdom)
              </li>
            </List>
            <Title tag="h3">Associated members</Title>
            <List marked>
              <li>Edward Allison (University of Washington, USA)</li>
              <li>
                Javier Arístegui (Universidad de Las Palmas de Gran Canaria,
                Spain)
              </li>
              <li>
                Xavier Capet (Pierre and Marie Curie University, France)
              </li>
              <li>
                Ming Feng (CSIRO Oceans & Atmosphere Indian Ocean Marine
                Research Centre, Australia)
              </li>
              <li>
                Iris Kriest (GEOMAR Helmholtz Centre for Ocean Research Kiel,
                Germany)
              </li>
              <li>
                Eric Machu (LOPS Laboratoire d'Océanographie Physique et
                Spatiale, France),
              </li>
              <li>
                Ryan Rykaczewski (University of South Carolina, Columbia, USA)
              </li>
              <li>
                Jong-Seong Kug (University of Science and Technology, South
                Korea)
              </li>
              <li>Lynne Shannon (University of Cape Town, South Africa)</li>
              <li>
                Damodar M. Shenoy (CSIR National Institute of Oceanography,
                India)
              </li>
              <li>
                Beatriz Yanicelli (Universidad de la República, Uruguay)
              </li>
            </List>
            <Title tag="h3">Invited members</Title>
            <List marked>
              <li>
                Veronique Garςon (LEGOS Laboratoire d’Etudes en Géophysique et
                Océanographie Spatiales, Francia)
              </li>
              <li>Olaf Duteil (GEOMAR, Germany)</li>
            </List>
            <Title tag="h3">SCOR</Title>
            <List marked>
              <li>Patricia Miloslavich (Delaware University, USA)</li>
            </List>
            <Figure>
              <Image
                style="width: 100%; height: auto;"
                :srcsets="['/images/proyectos/scor/mapa.png']"
                title="Map of members"
              />
            </Figure>
          </Navtab>
          <Navtab id="activities">
            <Title tag="h2">Activities</Title>
            <p>
              <b>Face to face Meeting:</b>
            </p>
            <p>
              <a
                :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/scor/SCOR_WG155_EBUS_FirstFaceToFaceMeeting_19Sept2018.pdf`"
                target="_blank"
              >
                - June 2018 (Washington DC, USA).
              </a>
            </p>
            <p>
              <b>Special Sessions:</b>
            </p>
            <p>
              - 4th International Symposium on ‘The Effects of Climate Change on
              World’s Oceans’
            </p>
            <a
              href="https://meetings.pices.int/publications/book-of-abstracts/2018-ECCWO-Book-of-Abstracts.pdf"
              target="_blank"
              >- Session 7: Eastern Boundary Upwelling Systems: Diversity
              Coupled Dynamics and Sensitivity to Climate Change.</a
            >
          </Navtab>
          <Navtab id="publications">
            <Title tag="h2">Publications</Title>
            <b>Science within Reach (dissemination articles)</b>
            <List>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/5132"

                  target="_blank"
                >
                  Montes, I. and Escribano, R. (2018), Una revisión sobre
                  los Sistemas de Bordes orientales: diversidad, dinámica acoplada y
                  sensibilidad al cambio climático, Boletín Técnico &quot;Generación de modelos
                  climáticos para el pronóstico de la ocurrencia del Fenómeno El Niño&quot;, Instituto
                  Geofísico del Perú, Junio, Vol. 5, Nº6, 5-9.
                </a>
              </li>
            </List>
          </Navtab>
          <Navtab id="announcements">
            <Title tag="h2">
              Announcements
            </Title>
            <li>
              <strong
                ><a href="https://www.ebus-lima2022.com" target="_blank"
                  >Open Science Conference on Eastern Boundary Upwelling Systems (EBUS): Past, Present
                and Future September 19 - 23, 2022, Lima, Peru</a
                ></strong
              >
            </li>
            <li>
              <strong
              ><a href="https://ebus.science/" target="_blank"
              >Ebus.science: approaching science to people</a
              ></strong
              >
            </li>
          </Navtab>
          <Navtab id="internal-use">
            <Title tag="h2">
              Documents
            </Title>
            <p>
              Documents used by the group can be found in the following
              <a
                href="https://drive.google.com/drive/u/1/folders/1fG09gRUUdxnOgnHMcSl54VrcAk0AyuVh?ogsrc=32"
                target="_blank"
                >link</a
              >
            </p>
          </Navtab>
          <Navtab id="useful-links">
            <Title tag="h2">Useful Links...</Title>
            <p>
              SCOR
              <a
                href="https://scor-int.org/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=https://scor-int.org&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNHYTXI-vC7Behs5-F6qvCsYgnz5lA"
                >https://scor-int.org</a
              >
            </p>
            <p>
              SOLAS
              <a
                href="http://solas-int.org"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://solas-int.org.customers.tigertech.net&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNEmjnJvFI4HoJCv6UrGaooZPr6xuA"
                >http://solas-int.org</a
              >
            </p>
            <p>
              CLIVAR
              <a
                href="http://www.clivar.org/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://www.clivar.org&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNEidhXFjd-dx6gNOzapOqa_3PGOgw"
                >http://www.clivar.org</a
              >
            </p>
            <p>
              IMBeR
              <a
                href="http://www.imber.info/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://www.imber.info&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNHz9kD_G-n2gxaSmJYsnK2i8aeGRQ"
                >http://www.imber.info</a
              >
            </p>
            <p>
              PICES
              <a
                href="http://meetings.pices.int/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://meetings.pices.int&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNGDZ9XvTV1wB_7u9mZ4jSfkHf5M1Q"
                >http://meetings.pices.int</a
              >
            </p>
            <p>
              IOC-UNESCO
              <a
                href="http://www.unesco.org/new/en/natural-sciences/ioc-oceans/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://www.unesco.org/new/en/natural-sciences/ioc-oceans/&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNF1Vc8WDbrAsZSMvKiOxEhlty4qvg"
                >http://www.unesco.org/new/en/natural-sciences/ioc-oceans/</a
              >
            </p>
            <p>
              OCEAN OBS'19
              <a
                href="http://www.oceanobs19.net/"
                target="_blank"
                data-saferedirecturl="https://www.google.com/url?q=http://www.oceanobs19.net/&amp;source=gmail&amp;ust=1538526825243000&amp;usg=AFQjCNGnl5UsBfdCM5ShuhVfh__JMZkclA"
                >http://www.oceanobs19.net</a
              >
            </p>
            <p>
              GOOS
              <a
                  href="https://www.goosocean.org/"
                  target="_blank"
                  data-saferedirecturl="https://www.google.com/url?q=https%3A%2F%2Fwww.goosocean.org%2F&ei=gzmFYunTK77b5OUP2pC-oAs&ved=0ahUKEwipoejG1en3AhW-LbkGHVqID7QQ4dUDCA4&uact=5&oq=https%3A%2F%2Fwww.goosocean.org%2F&gs_lcp=Cgdnd3Mtd2l6EAM6BwgAEEcQsAM6BwgAELADEEM6DwguENQCEMgDELADEEMYAToMCC4QyAMQsAMQQxgBOgUIABCABDoECAAQHjoGCAAQBRAeSgQIQRgASgQIRhgBULYKWONnYMpraANwAXgDgAGpAYgBtg2SAQM5LjiYAQCgAQGgAQKwAQDIARHAAQHaAQYIARABGAg&sclient=gws-wiz"
              >https://www.goosocean.org/</a
              >
            </p>
            <p>
              Ocean Decade
              <a
                  href="https://www.oceandecade.org/"
                  target="_blank"
                  data-saferedirecturl="https://www.google.com/url?q=https%3A%2F%2Fwww.oceandecade.org%2F&ei=PTqFYuWuLtaE5OUPv_GLoAY&ved=0ahUKEwilwcOf1un3AhVWArkGHb_4AmQQ4dUDCA4&uact=5&oq=https%3A%2F%2Fwww.oceandecade.org%2F&gs_lcp=Cgdnd3Mtd2l6EAMyCAgAEBYQChAeSgQIQRgASgQIRhgAUABYAGC1AmgAcAF4AIABX4gBX5IBATGYAQCgAQKgAQHAAQE&sclient=gws-wiz"
              >https://www.oceandecade.org/</a
              >
            </p>
          </Navtab>
          <Navtab id="find-data">
            <Title tag="h2">Where to find data?</Title>
            <List marked>
              <li>
                <a
                    href="https://cds.climate.copernicus.eu/cdsapp#!/home"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fcds.climate.copernicus.eu%2Fcdsapp%23%21%2Fhome&source=hp&ei=PD2FYvDuMbWa1sQPsKucsAE&iflsig=AJiK0e8AAAAAYoVLTGBXjp4yIiAdImToAuHMCluX5Tax&ved=0ahUKEwjw_KSN2en3AhU1jZUCHbAVBxYQ4dUDCAc&uact=5&oq=https%3A%2F%2Fcds.climate.copernicus.eu%2Fcdsapp%23%21%2Fhome&gs_lcp=Cgdnd3Mtd2l6EANQ7wFY7wFgtwVoAXAAeACAAV6IAV6SAQExmAEAoAECoAEBsAEA&sclient=gws-wiz"
                >https://cds.climate.copernicus.eu/cdsapp#!/home</a>
              </li>
              <li>
                <a
                    href="https://emodnet.ec.europa.eu/en/about_emodnet"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Femodnet.ec.europa.eu%2Fen%2Fabout_emodnet&source=hp&ei=hj2FYuKTMNvz1sQPub6T8AU&iflsig=AJiK0e8AAAAAYoVLlkgie8hGG0UXOSHEmZIngWRXc6co&ved=0ahUKEwji7sew2en3AhXbuZUCHTnfBF4Q4dUDCAc&uact=5&oq=https%3A%2F%2Femodnet.ec.europa.eu%2Fen%2Fabout_emodnet&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYIYEaABwAHgAgAFUiAFUkgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://emodnet.ec.europa.eu/en/about_emodnet</a>
              </li>
              <li>
                <a
                    href="https://fleetmonitoring.euro-argo.eu/dashboard?Status=Active"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Ffleetmonitoring.euro-argo.eu%2Fdashboard%3FStatus%3DActive&source=hp&ei=5z2FYpSkNrGE0Aaxl5WYDg&iflsig=AJiK0e8AAAAAYoVL99o5DDOrPpL1BZ55mzG5p2SpOgjs&ved=0ahUKEwjUs-7e2en3AhUxAtQKHbFLBeMQ4dUDCAc&uact=5&oq=https%3A%2F%2Ffleetmonitoring.euro-argo.eu%2Fdashboard%3FStatus%3DActive&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYKYCaABwAHgAgAFUiAFUkgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://fleetmonitoring.euro-argo.eu/dashboard?Status=Active</a>
              </li>
              <li>
                <a
                    href="https://www.pangaea.de/"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fwww.pangaea.de%2F&source=hp&ei=KT6FYoSDGvqr5OUPqZqT4AU&iflsig=AJiK0e8AAAAAYoVMOd30fjYia6VHQtgcUGjlZovbSFJT&ved=0ahUKEwjEu47-2en3AhX6FbkGHSnNBFwQ4dUDCAc&uact=5&oq=https%3A%2F%2Fwww.pangaea.de%2F&gs_lcp=Cgdnd3Mtd2l6EAMyBggAEA0QHjIICAAQCBANEB4yCggAEAgQDRAKEB4yCggAEA8QCBANEB5Q5wFY5wFg7gNoAXAAeACAAVOIAVOSAQExmAEAoAECoAEBsAEA&sclient=gws-wiz"
                >https://www.pangaea.de/</a>
              </li>
              <li>
                <a
                    href="https://www.ego-network.org/dokuwiki/doku.php?id=start"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fwww.ego-network.org%2Fdokuwiki%2Fdoku.php%3Fid%3Dstart&source=hp&ei=eD6FYpqEBr3A5OUPq_qGmAw&iflsig=AJiK0e8AAAAAYoVMiPTKRgWlBn2mp9nQS5bDyqTDB-Ry&ved=0ahUKEwiaoNCj2un3AhU9ILkGHSu9AcMQ4dUDCAc&uact=5&oq=https%3A%2F%2Fwww.ego-network.org%2Fdokuwiki%2Fdoku.php%3Fid%3Dstart&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYJcCaABwAHgAgAFUiAFUkgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://www.ego-network.org/dokuwiki/doku.php?id=start</a>
              </li>
              <li>
                <a
                    href="https://oceandata.sci.gsfc.nasa.gov"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Foceandata.sci.gsfc.nasa.gov&source=hp&ei=0D6FYvbmKoO95OUPteKv2AE&iflsig=AJiK0e8AAAAAYoVM4FjSXmn40Atnoah-Rz3OrVsYJGWY&ved=0ahUKEwj2jvDN2un3AhWDHrkGHTXxCxsQ4dUDCAc&uact=5&oq=https%3A%2F%2Foceandata.sci.gsfc.nasa.gov&gs_lcp=Cgdnd3Mtd2l6EAMyBggAEA0QHjIICAAQFhAKEB5QAFgAYK0BaABwAHgAgAFciAFckgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://oceandata.sci.gsfc.nasa.gov</a>
              </li>
              <li>
                <a
                  href="https://www.pmel.noaa.gov/gtmba/"
                  target="_blank"
                  data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fwww.pmel.noaa.gov%2Fgtmba%2F&source=hp&ei=JT-FYt_fNuGJ5OUP7PiSmA4&iflsig=AJiK0e8AAAAAYoVNNWNWrHAg4cmO8POhsUCnH5A8y5jO&ved=0ahUKEwifhsD22un3AhXhBLkGHWy8BOMQ4dUDCAc&uact=5&oq=https%3A%2F%2Fwww.pmel.noaa.gov%2Fgtmba%2F&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYMwBaABwAHgAgAFSiAFSkgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://www.pmel.noaa.gov/gtmba/</a>
              </li>
              <li>
                <a
                    href="https://odv.awi.de/data/ocean/"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fodv.awi.de%2Fdata%2Focean%2F&source=hp&ei=cT-FYsHtLc285OUP94CcuA0&iflsig=AJiK0e8AAAAAYoVNgUoHMZvYU6lzmzi2hvzWe5PD44QI&ved=0ahUKEwiB6tWa2-n3AhVNHrkGHXcAB9cQ4dUDCAc&uact=5&oq=https%3A%2F%2Fodv.awi.de%2Fdata%2Focean%2F&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYIYDaABwAHgAgAFYiAFYkgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://odv.awi.de/data/ocean/</a>
              </li>
              <li>
                <a
                    href="https://www.nodc.noaa.gov/worlddatacenter/"
                    target="_blank"
                    data-saferedirecturl="https://www.google.com.pe/url?q=https%3A%2F%2Fwww.nodc.noaa.gov%2Fworlddatacenter%2F&source=hp&ei=0j-FYvXYAuae5OUPvPO36Aw&iflsig=AJiK0e8AAAAAYoVN4sCQjmIu4xJIMDVX4UOJJu4OqOPY&ved=0ahUKEwj1icvI2-n3AhVmD7kGHbz5Dc0Q4dUDCAc&uact=5&oq=https%3A%2F%2Fwww.nodc.noaa.gov%2Fworlddatacenter%2F&gs_lcp=Cgdnd3Mtd2l6EANQAFgAYOoBaABwAHgAgAFaiAFakgEBMZgBAKABAqABAQ&sclient=gws-wiz"
                >https://www.nodc.noaa.gov/worlddatacenter/</a>
              </li>
            </List>
          </Navtab>
          <br />
          <br />
          <Row>
            <ColumnUi md="4">
              <Figure height="150px">
                <Image :srcsets="['/images/proyectos/scor/scor.png']" />
                <template #figcaption>
                  Scientific Committee on Oceanic Research <br />(SCOR)
                </template>
              </Figure>
            </ColumnUi>
            <ColumnUi md="4">
              <Figure height="150px">
                <Image :srcsets="['/images/proyectos/scor/NSF_2.png']" />
                <template #figcaption>
                  National Science Foundation <br />
                  (NSF)
                </template>
              </Figure>
            </ColumnUi>
            <ColumnUi md="4">
              <Figure height="150px">
                <Image :srcsets="['/images/proyectos/scor/Pices.png']" />
                <template #figcaption>
                </template>
              </Figure>
            </ColumnUi>
          </Row>
        </ColumnUi>
        <ColumnUi md="3">
          <Nav :items="navItems"/>
        </ColumnUi>
      </Row>
    </Container>
  </main>
</template>
<script>
import Trail from "@/components/common/Trail";
import Title from "@/components/ui/atoms/Title";
import List from "@/components/ui/objects/List";
import Container from "@/components/ui/objects/Container";
import Figure from "@/components/ui/objects/Figure";
import Image from "@/components/ui/objects/Image";
import Nav from "@/components/ui/objects/Nav";
import Navtab from "@/components/ui/objects/Navtab";
import Row from "@/components/ui/atoms/Row";
import ColumnUi from "@/components/ui/atoms/ColumnUi";
export default {
  components: {
    Trail,
    Title,
    List,
    Container,
    Figure,
    Image,
    Nav,
    Navtab,
    Row,
    ColumnUi
  },
  setup() {
    const navItems = [
      {
        path: "#preamble",
        tag: "Preamble"
      },
      {
        path: "#terms-reference",
        tag: "Terms of Reference"
      },
      {
        path: "#members",
        tag: "Members"
      },
      {
        path: "#activities",
        tag: "Activities"
      },
      {
        path: "#publications",
        tag: "Publications"
      },
      {
        path: "#announcements",
        tag: "Announcements"
      },
      {
        path: "#internal-use",
        tag: "Internal use"
      },
      {
        path: "#useful-links",
        tag: "Useful links"
      },
      {
        path: "#find-data",
        tag: "Where to find data?"
      }
    ];
    return {
      navItems
    };
  }
};
</script>
